/**
 * File: BlogPost.jsx
 * Path: /src/pages/BlogPost.jsx
 * Description: Template for individual blog post pages with the first post being "The Weight of Justice"
 * Key Features: Article formatting, author info, related posts, and share functionality
 */

import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import ModernCtaSection from '../components/common/ModernCtaSection';
import useIntersectionObserver from '../hooks/useIntersectionObserver';

// Related post card component
const RelatedPostCard = ({ post, index }) => {
  const [ref, isVisible] = useIntersectionObserver({
    threshold: 0.1,
    triggerOnce: true,
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <Card 
        interactive 
        className="h-full flex flex-col transition-all duration-500"
      >
        {post.image && (
          <div className="w-full h-36 mb-4 overflow-hidden rounded-t-xl">
            <img 
              src={post.image} 
              alt={post.title} 
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          </div>
        )}
        
        <div className="flex-1 flex flex-col p-4 pt-0">
          <div className="flex items-center text-dark-400 text-xs mb-2">
            <span>{post.date}</span>
            <span className="mx-2">•</span>
            <span>{post.category}</span>
          </div>
          
          <h3 className="text-lg font-bold mb-2">{post.title}</h3>
          <p className="text-dark-500 text-sm mb-4 flex-grow line-clamp-2">{post.excerpt}</p>
          
          <Link 
            to={`/blog/${post.slug}`}
            className="text-primary-600 text-sm font-medium hover:text-primary-700 transition-colors mt-auto"
          >
            Read Article →
          </Link>
        </div>
      </Card>
    </motion.div>
  );
};

// Share button component
const ShareButton = ({ platform, icon, color, url, title }) => {
  const handleShare = () => {
    let shareUrl = '';
    
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`Check out this article: ${url}`)}`;
        break;
      default:
        shareUrl = '';
    }
    
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'noopener,noreferrer');
    }
  };
  
  return (
    <button 
      onClick={handleShare}
      className={`w-10 h-10 rounded-full flex items-center justify-center transition-transform hover:scale-110 ${color}`}
      aria-label={`Share on ${platform}`}
    >
      {icon}
    </button>
  );
};

const BlogPost = () => {
  const { slug } = useParams();
  const [sectionRef, isVisible] = useIntersectionObserver({
    threshold: 0.1,
    triggerOnce: true
  });
  
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  
  // Mock blog post data - in a real implementation this would come from a CMS or API
  // For now, we're only implementing the "weight-of-justice" post in detail
  const blogPostData = {
    title: "The Weight of Justice: AI Solutions for India's Judicial Caseload Crisis",
    date: "May 5, 2025",
    category: "Legal Research",
    author: {
      name: "Adv Vibhor Gupta",
      title: "Managing Partner, AMV LAW",
      avatar: "/placeholder-avatar.jpg",
      bio: "Vibhor Gupta is a leading legal technology expert with over 15 years of experience in the Indian court system. As Managing Partner at AMV LAW, he specializes in technology law and judicial reforms."
    },
    heroImage: "/placeholder-blog-1.jpg",
    readTime: "10 min read",
    content: [
      {
        type: "intro",
        text: "The Indian judicial system faces an unprecedented backlog of cases across all levels of courts. This article presents a comparative analysis of judicial caseloads between India and other common law jurisdictions including the UK, Australia, Canada, and the US, highlighting the stark disparities in pending cases and judge-to-population ratios. It examines India's unique structural factors contributing to this burden and analyzes various AI-powered solutions currently being implemented or considered for the judicial system."
      },
      {
        type: "heading",
        text: "Introduction"
      },
      {
        type: "paragraph",
        text: "The common law tradition, characterized by judicial precedent, stare decisis, and judge-made law, encompasses numerous jurisdictions globally, with India representing one of its most significant practitioners. This article compares the immense judicial caseloads in India with those in other common law countries like the UK, Australia, Canada, and the US, highlighting the stark differences in pending cases and judge-to-population ratios."
      },
      {
        type: "paragraph",
        text: "It examines India's unique systemic factors contributing to this burden and analyzes legal reforms and case management innovations both in India and internationally. Furthermore, the text explores the current and potential integration of artificial intelligence within the Indian judicial system to enhance efficiency, while also considering the legal and ethical implications of such technological adoption and offering an implementation roadmap."
      },
      {
        type: "heading",
        text: "India's Judicial Burden: A Quantitative Assessment"
      },
      {
        type: "paragraph",
        text: "India's judiciary operates under extraordinary pressure, managing caseloads of unprecedented scale. Recent statistics from the National Judicial Data Grid (NJDG) and Supreme Court Annual Reports reveal the magnitude of this burden."
      },
      {
        type: "statistics",
        items: [
          "Supreme Court of India: As of February 2024, 82,761 cases remain pending, with approximately 73% constituting regular hearing matters and 27% admission matters.",
          "High Courts: The 25 High Courts collectively face a backlog of 6.18 million cases, with an average case pendency period of 4.7 years.",
          "District and Subordinate Courts: These courts confront 40.73 million pending cases, with approximately 8.5 million cases pending for over 5 years.",
          "Judge-Population Ratio: India maintains approximately 21 judges per million population, compared to the recommended 50-55 judges per million.",
          "Case Clearance Rate: The average clearance rate across all courts stands at approximately 87%, indicating that for every 100 cases filed, only 87 are disposed of annually."
        ]
      },
      {
        type: "paragraph",
        text: "This immense volume stems from several jurisdictional and structural factors:"
      },
      {
        type: "list",
        items: [
          "Constitutional Framework: Articles 32 and 226 of the Constitution of India confer broad writ jurisdiction upon the Supreme Court and High Courts respectively, enabling direct access for fundamental rights enforcement.",
          "Public Interest Litigation (PIL): The judicial innovation of PIL has significantly expanded standing requirements since S.P. Gupta v. Union of India (1981 Supp SCC 87), allowing any public-spirited individual to approach courts regarding matters of public importance.",
          "Statutory Appeals: Over 250 central statutes provide for direct appeals to the Supreme Court, bypassing the conventional appellate structure.",
          "Jurisdictional Overlap: Concurrent jurisdiction between various courts and tribunals creates multiple forums for identical causes of action."
        ]
      },
      {
        type: "heading",
        text: "Comparative Assessment: Other Common Law Jurisdictions"
      },
      {
        type: "paragraph",
        text: "The quantitative comparison reveals the extraordinary nature of India's judicial challenge. While the UK processes thousands of cases with several thousand judges, and Australia and Canada handle hundreds of thousands with approximately 1,000-2,000 judges each, India's judiciary confronts tens of millions of cases with roughly 25,000 judges nationwide."
      },
      {
        type: "heading",
        text: "Legal Reforms and Innovations"
      },
      {
        type: "paragraph",
        text: "India has undertaken several reform initiatives to address its caseload challenges, including the Commercial Courts Act, Arbitration and Conciliation Act amendments, CPC amendments, and the E-Courts Project. Other common law jurisdictions offer valuable models for consideration, such as pre-action protocols, costs shifting regimes, individual docket systems, and robust case management powers."
      },
      {
        type: "heading",
        text: "Artificial Intelligence in the Indian Judicial System"
      },
      {
        type: "paragraph",
        text: "The integration of artificial intelligence in India's judicial system has gained significant momentum, with several initiatives already operational:"
      },
      {
        type: "list",
        items: [
          "SUVAAS (Supreme Court Vidhik Anuvaad Software): Launched in 2019, this AI-powered translation tool translates Supreme Court judgments into nine vernacular languages.",
          "SUPACE (Supreme Court Portal for Assistance in Courts Efficiency): Developed in 2021, this AI tool assists judges in legal research, precedent analysis, and case categorization.",
          "SCI-Interact: The Supreme Court's AI-driven interactive platform streamlines case filing and schedule management.",
          "LIMBS (Legal Information Management & Briefing System): Deployed across government departments, this AI system tracks government litigation.",
          "NIC ASTR (Automatic Speech Transcription for Records): For real-time transcription of court proceedings."
        ]
      },
      {
        type: "paragraph",
        text: "Several emerging AI applications demonstrate particular promise for addressing India's judicial backlog, including predictive analytics for case routing, intelligent case scheduling, automated preliminary scrutiny, document summarization, and intelligent decision support systems."
      },
      {
        type: "heading",
        text: "Implementation Roadmap for India"
      },
      {
        type: "paragraph",
        text: "A strategic approach to AI integration would include a three-phase implementation roadmap:"
      },
      {
        type: "list",
        items: [
          "Phase I (Near-Term): Expand current document management and NLP applications, implement automated administrative functions, and standardize data collection methods.",
          "Phase II (Medium-Term): Deploy predictive case management systems, implement advanced legal research assistants, and develop specialized AI applications for high-volume case categories.",
          "Phase III (Long-Term): Integrate complex decision support systems, implement cross-jurisdictional data sharing, and develop adaptive AI systems."
        ]
      },
      {
        type: "heading",
        text: "Ethical Considerations and Safeguards"
      },
      {
        type: "paragraph",
        text: "The integration of AI within judicial processes necessitates robust ethical frameworks, including transparency, human oversight, bias mitigation, privacy protection, and specialized training for judicial officers."
      },
      {
        type: "heading",
        text: "Conclusion"
      },
      {
        type: "paragraph",
        text: "The integration of artificial intelligence represents perhaps the most promising frontier for addressing India's case management challenges. While AI cannot replace judicial reasoning, it can dramatically enhance efficiency in administrative functions, legal research, document processing, and preliminary analysis. The experience of other common law jurisdictions demonstrates that thoughtful AI implementation, balanced with appropriate ethical safeguards, can significantly reduce backlog while preserving the core values of judicial independence and procedural fairness."
      },
      {
        type: "paragraph",
        text: "As India continues its judicial evolution, a technology-forward approach that leverages AI innovations while respecting the unique constitutional framework and legal culture offers the most promising path toward a more efficient and accessible justice system."
      }
    ],
    relatedPosts: [
      {
        id: 2,
        title: "Predictive Analytics: The Future of Case Outcome Forecasting",
        slug: "predictive-analytics-future-case-outcome-forecasting",
        excerpt: "How machine learning models are revolutionizing litigation strategy by forecasting judicial decisions with increasing accuracy.",
        date: "April 28, 2025",
        category: "Judgment Prediction",
        image: "/placeholder-blog-2.jpg"
      },
      {
        id: 3,
        title: "Beyond Document Review: AI for Complex Contract Analysis",
        slug: "beyond-document-review-ai-complex-contract-analysis",
        excerpt: "An exploration of how advanced NLP techniques are transforming contract review from simple term extraction to comprehensive risk assessment.",
        date: "April 15, 2025",
        category: "Contract Analysis",
        image: "/placeholder-blog-3.jpg"
      },
      {
        id: 5,
        title: "Building Ethical Guardrails for Legal AI Systems",
        slug: "building-ethical-guardrails-legal-ai-systems",
        excerpt: "Essential considerations for developing AI legal tools that maintain judicial independence and fairness.",
        date: "March 22, 2025",
        category: "Ethics",
        image: "/placeholder-blog-5.jpg"
      }
    ]
  };

  // For demonstration, we're only implementing the full "weight-of-justice" post
  // In a real app, you would fetch the post data based on the slug parameter
  const currentPost = blogPostData;
  
  // Function to render different content types
  const renderContent = (item, index) => {
    switch (item.type) {
      case 'intro':
        return (
          <div key={index} className="text-lg text-dark-600 mb-8 font-medium border-l-4 border-primary-500 pl-4 py-2">
            {item.text}
          </div>
        );
      case 'heading':
        return (
          <h2 key={index} className="text-2xl md:text-3xl font-bold mt-10 mb-4">
            {item.text}
          </h2>
        );
      case 'paragraph':
        return (
          <p key={index} className="text-dark-600 mb-6 leading-relaxed">
            {item.text}
          </p>
        );
      case 'statistics':
        return (
          <div key={index} className="mb-8 bg-primary-50 p-5 rounded-lg">
            <ul className="space-y-3">
              {item.items.map((stat, statIndex) => (
                <li key={statIndex} className="flex items-start">
                  <span className="text-primary-600 mr-2 mt-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                  <span>{stat}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      case 'list':
        return (
          <ul key={index} className="space-y-3 mb-8 list-disc list-inside text-dark-600">
            {item.items.map((item, itemIndex) => (
              <li key={itemIndex} className="pl-2">
                {item}
              </li>
            ))}
          </ul>
        );
      default:
        return null;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <section className="pt-32 pb-12 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 -z-10 w-1/3 h-1/3 bg-primary-100 rounded-bl-full opacity-70"></div>
        <div className="absolute bottom-0 left-0 -z-10 w-1/4 h-1/4 bg-secondary-100 rounded-tr-full opacity-70"></div>
        
        <div className="container-padding mx-auto">
          <div className="max-w-4xl mx-auto">
            <motion.div 
              className="flex items-center text-dark-400 text-sm mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Link to="/blog" className="hover:text-primary-600 transition-colors">
                Blog
              </Link>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mx-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <span>{currentPost.category}</span>
            </motion.div>
            
            <motion.h1 
              className="text-3xl md:text-5xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              {currentPost.title}
            </motion.h1>
            
            <motion.div 
              className="flex items-center flex-wrap gap-4 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full overflow-hidden mr-3">
                  <img 
                    src={currentPost.author.avatar} 
                    alt={currentPost.author.name} 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <div className="text-dark-700 font-medium">{currentPost.author.name}</div>
                  <div className="text-dark-500 text-sm">{currentPost.author.title}</div>
                </div>
              </div>
              
              <div className="flex items-center text-dark-500 text-sm">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>{currentPost.date}</span>
              </div>
              
              <div className="flex items-center text-dark-500 text-sm">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{currentPost.readTime}</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Featured Image */}
      <section className="pb-16">
        <div className="container-padding mx-auto">
          <div className="max-w-4xl mx-auto">
            <motion.div 
              className="w-full h-80 md:h-96 rounded-xl overflow-hidden shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <img 
                src={currentPost.heroImage} 
                alt={currentPost.title} 
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Blog Content Section */}
      <section className="py-12" ref={sectionRef}>
        <div className="container-padding mx-auto">
          <div className="max-w-4xl mx-auto">
            <div className="flex">
              {/* Share sidebar */}
              <motion.div 
                className="hidden md:flex flex-col items-center space-y-4 sticky top-32 h-fit mr-8"
                initial={{ opacity: 0, x: -20 }}
                animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                transition={{ duration: 0.5 }}
              >
                <div className="text-dark-400 text-sm font-medium">Share</div>
                <ShareButton 
                  platform="twitter" 
                  color="bg-blue-400 text-white" 
                  icon={
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                    </svg>
                  }
                  url={window.location.href}
                  title={currentPost.title}
                />
                <ShareButton 
                  platform="linkedin" 
                  color="bg-blue-600 text-white" 
                  icon={
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  }
                  url={window.location.href}
                  title={currentPost.title}
                />
                <ShareButton 
                  platform="facebook" 
                  color="bg-blue-800 text-white" 
                  icon={
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  }
                  url={window.location.href}
                  title={currentPost.title}
                />
                <ShareButton 
                  platform="email" 
                  color="bg-dark-500 text-white" 
                  icon={
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                    </svg>
                  }
                  url={window.location.href}
                  title={currentPost.title}
                />
              </motion.div>
              
              {/* Main content */}
              <div className="flex-1">
                {/* Article content */}
                <motion.article 
                  className="prose prose-lg max-w-none mb-12"
                  initial={{ opacity: 0, y: 20 }}
                  animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration: 0.7 }}
                >
                  {currentPost.content.map(renderContent)}
                </motion.article>
                
                {/* Tags and mobile share buttons */}
                <motion.div 
                  className="flex flex-col sm:flex-row sm:items-center justify-between border-t border-b border-dark-100 py-6 mb-12"
                  initial={{ opacity: 0, y: 20 }}
                  animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <div className="mb-4 sm:mb-0">
                    <div className="text-dark-500 mb-3">Tags:</div>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-primary-50 text-primary-600 rounded-full text-sm">Legal AI</span>
                      <span className="px-3 py-1 bg-primary-50 text-primary-600 rounded-full text-sm">Judicial Reform</span>
                      <span className="px-3 py-1 bg-primary-50 text-primary-600 rounded-full text-sm">India</span>
                      <span className="px-3 py-1 bg-primary-50 text-primary-600 rounded-full text-sm">Case Management</span>
                    </div>
                  </div>
                  
                  <div className="flex md:hidden space-x-3">
                    <ShareButton 
                      platform="twitter" 
                      color="bg-blue-400 text-white" 
                      icon={
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                        </svg>
                      }
                      url={window.location.href}
                      title={currentPost.title}
                    />
                    <ShareButton 
                      platform="linkedin" 
                      color="bg-blue-600 text-white" 
                      icon={
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                        </svg>
                      }
                      url={window.location.href}
                      title={currentPost.title}
                    />
                    <ShareButton 
                      platform="facebook" 
                      color="bg-blue-800 text-white" 
                      icon={
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                        </svg>
                      }
                      url={window.location.href}
                      title={currentPost.title}
                    />
                  </div>
                </motion.div>
                
                {/* Author bio */}
                <motion.div 
                  className="bg-primary-50 rounded-xl p-6 mb-12"
                  initial={{ opacity: 0, y: 20 }}
                  animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <div className="flex flex-col sm:flex-row items-start sm:items-center">
                    <div className="w-16 h-16 rounded-full overflow-hidden mr-5 mb-4 sm:mb-0">
                      <img 
                        src={currentPost.author.avatar} 
                        alt={currentPost.author.name} 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2">{currentPost.author.name}</h3>
                      <p className="text-dark-600 text-sm mb-3">{currentPost.author.title}</p>
                      <p className="text-dark-500">{currentPost.author.bio}</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Related Posts Section */}
      <section className="py-16 bg-dark-50">
        <div className="container-padding mx-auto">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-12"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Related Articles
            </h2>
            <p className="text-dark-500 text-lg">
              Explore more insights on legal AI and technology
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {currentPost.relatedPosts.map((post, index) => (
              <RelatedPostCard key={post.id} post={post} index={index} />
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <ModernCtaSection
        title="Stay Updated on Legal Technology Trends"
        description="Join our newsletter to receive the latest insights on legal AI, product updates, and industry news delivered to your inbox."
      >
        <div className="max-w-md mx-auto">
          <div className="flex flex-col sm:flex-row gap-3">
            <input 
              type="email" 
              placeholder="Your email address" 
              className="flex-grow px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/30"
            />
            <Button 
              variant="primary" 
              className="whitespace-nowrap"
            >
              Subscribe
            </Button>
          </div>
          <div className="text-sm text-primary-100 mt-3">
            We respect your privacy. Unsubscribe at any time.
          </div>
        </div>
      </ModernCtaSection>
    </motion.div>
  );
};

export default BlogPost;